import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Shield, Loader2, X, Monitor, Smartphone, Tablet } from 'lucide-react';
import { toast } from 'sonner';

const deviceIcon = (device) => {
  const d = (device || '').toLowerCase();
  if (d.includes('iphone') || d.includes('phone')) return Smartphone;
  if (d.includes('ipad') || d.includes('tablet')) return Tablet;
  return Monitor;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const ActiveSessions = ({ api }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await api().get('/auth/sessions');
      setSessions(res.data.sessions || []);
    } catch {
      toast.error('Failed to load sessions');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleRevoke = async (sessionId) => {
    if (!window.confirm('Sign out this device?')) return;
    setRevoking(sessionId);
    try {
      await api().delete(`/auth/sessions/${sessionId}`);
      toast.success('Session revoked');
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    }
    setRevoking(null);
  };

  const handleRevokeOthers = async () => {
    const count = sessions.filter((s) => !s.is_current).length;
    if (count === 0) {
      toast.info('No other active sessions');
      return;
    }
    if (!window.confirm(`Sign out ${count} other session${count === 1 ? '' : 's'}?`)) return;
    setRevokingAll(true);
    try {
      const res = await api().post('/auth/sessions/revoke-others');
      toast.success(`Revoked ${res.data.count || count} session${(res.data.count || count) === 1 ? '' : 's'}`);
      setSessions((prev) => prev.filter((s) => s.is_current));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke');
    }
    setRevokingAll(false);
  };

  const otherCount = sessions.filter((s) => !s.is_current).length;

  return (
    <div className="pb-6 border-b border-border" data-testid="active-sessions-section">
      <h4 className="font-heading text-lg text-primary uppercase tracking-wider mb-2 flex items-center gap-2">
        <Shield className="h-5 w-5" />
        Active Sessions
      </h4>
      <p className="text-muted-foreground text-sm mb-4">
        Places you're currently signed in. If you see a session you don't recognize, revoke it.
      </p>

      {loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="h-6 w-6 text-primary animate-spin" />
        </div>
      ) : sessions.length === 0 ? (
        <p className="text-muted-foreground text-sm py-4">No active sessions.</p>
      ) : (
        <>
          <div className="space-y-2">
            {sessions.map((s) => {
              const Icon = deviceIcon(s.device);
              return (
                <div
                  key={s.id}
                  className={`bg-black border p-3 rounded flex items-start justify-between gap-3 ${
                    s.is_current ? 'border-primary/50' : 'border-border'
                  }`}
                  data-testid={`session-${s.id}`}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`h-9 w-9 rounded flex items-center justify-center flex-shrink-0 ${
                      s.is_current ? 'bg-primary/20' : 'bg-muted-foreground/10'
                    }`}>
                      <Icon className={`h-4 w-4 ${s.is_current ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-sm font-medium">{s.browser} on {s.device}</span>
                        {s.is_current && (
                          <span className="text-[10px] uppercase tracking-wider bg-primary/20 text-primary px-1.5 py-0.5 rounded font-heading">
                            Current
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground text-xs mt-0.5">
                        {s.ip && <span>{s.ip} · </span>}
                        Active {timeAgo(s.last_active_at)}
                      </p>
                    </div>
                  </div>

                  {!s.is_current && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevoke(s.id)}
                      disabled={revoking === s.id}
                      className="text-muted-foreground hover:text-red-400 h-8 w-8 p-0 flex-shrink-0"
                      data-testid={`revoke-session-${s.id}`}
                      title="Sign out this device"
                    >
                      {revoking === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {otherCount > 0 && (
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRevokeOthers}
                disabled={revokingAll}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid="revoke-other-sessions-btn"
              >
                {revokingAll ? (
                  <><Loader2 className="h-3 w-3 animate-spin mr-2" /> Revoking...</>
                ) : (
                  <>Sign out all other sessions ({otherCount})</>
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
